/**
 * **The engine's records, as the package reads them** (engine W15, FR-E13,
 * FR-E19 – FR-E21; contract C-E2 and C-E3 of the W12 session prompts).
 *
 * The shapes below are `prodoc-service`'s own answers, read from its routes
 * (`app/api/parsing.py`, `app/domain/extractions.py`, `app/api/payloads.py`):
 *
 * - a **parse job** (`job_out`) — where a request is, why it ended, and once
 *   completed its extraction's id (FR-E9, FR-E10);
 * - an extraction's **record** (`extraction_out`) — what a list row and a
 *   not-yet-readable extraction carry: the profile, the channel, whether it
 *   reconciles, the checks' names and results **without figures**, the flags,
 *   the two confidence axes, the rungs and the review (FR-E13);
 * - the **full extraction** (`full_out`) — the record with its content: the
 *   fields, the evidence for each, every check with its expected, found and
 *   delta in paise, the corrections, and — once corrected — the accepted view
 *   and its rechecked checks beside the originals (FR-E21).
 *
 * Every normaliser here reads defensively — an absent key is `null`, never a
 * guess — so a screen built on them renders what the service said and nothing
 * else. **Money is integer paise everywhere** (FR-E16).
 *
 * Nothing here talks to the network or imports the kit: `prodoc-client.ts`
 * reads through these and stays as free of the screens as it always was;
 * `extraction-model.ts` adds how a person reads them.
 */

import { maskedPathsOf } from './personal-data'
import { splitAnswerOf, type SplitAnswer } from './split-shapes'

/* -------------------------------------------------------------- the job */

/** FR-E10: a job ends `completed`, `refused` or `failed`; until then it is `queued` or `running`. */
export type JobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'refused'

/** The statuses a job ends in — nothing moves it after one of these. */
export const JOB_ENDED: readonly string[] = ['completed', 'failed', 'refused']

export const jobEnded = (job: { status: string } | null | undefined): boolean => Boolean(job && JOB_ENDED.includes(job.status))

/** ADR-0016 §4: `auto` is ProDoc's review (the fallback), `app` the calling app's own. */
export type ReviewMode = 'auto' | 'app'

/** A parse job as `GET /jobs/{id}` answers it (FR-E9). Never the context or the password, which the service seals and erases. */
export interface ParseJob {
  id: string
  document_id: string
  version_id: string
  owner_app: string
  /** `parse`, or `recheck` — the kernel re-checking a corrected view (D11). */
  kind: string
  /** The profile asked for — `auto` when the engine was asked to find it. */
  profile: string | null
  options: Record<string, unknown>
  review_mode: ReviewMode | string
  /** `interactive` (a person, or a caller that waits) or `bulk` (FR-E12). */
  lane: string | null
  status: JobStatus | string
  /** The refusal's or the failure's code (FR-E10): `profile_mismatch`, `password_incorrect`, `timeout`, … */
  reason: string | null
  /** With `profile_mismatch`: what the engine thinks it is, when one cleared the bar. */
  suggested_profile: string | null
  attempts: number
  /** `external` or `india_only`, decided at claim (D13). */
  residency: string | null
  extraction_id: string | null
  /** A re-parse asked for with `force` and a reason (FR-E5). */
  forced: boolean
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  /**
   * W32 (engine FR-E49, D121): a parse of a mapped profile refused `mapping_needed` —
   * the sheets, the header candidates and a suggested map, on the job read only.
   * Absent on every other job, so set only when the service sent it.
   */
  mapping?: MappingNeeded | null
  /** W32 (FR-E49, D122): a preview job's reading under an unsaved map, once `completed`. Absent elsewhere. */
  preview?: MapPreview | null
  /** W37 (C8, D170): a `split` job's boundaries, doubts and parts, once `completed`. Absent elsewhere. */
  split?: SplitAnswer | null
  /** W38 (FR-E59, D178): a `survey` job's sheets, once `completed`; erased an hour after. Absent elsewhere. */
  survey?: WorkbookSurvey | null
  /** W19 (C-E26, D193): where its model rungs go — `inhouse`, `withheld`, `external`, `none`. Set only when the service sent it. */
  ai_route?: string | null
  /** W19 (D199): when an `india_only` job first waited for ProDoc's own model. Set only when sent. */
  held_since?: string | null
  /** W19 (FR-E70, D200): `inhouse_model` while a held job waits for ProDoc's own model, in India; else `null`. */
  waiting?: string | null
}

/**
 * W38 (engine FR-E59, D178): one sheet of a surveyed workbook — the profile that would read it
 * (`other` where none does, or where the caller decides), the role ProGST's rules gave it, its
 * header row (1-based, `null` where none), the rows under it carrying a figure or a date, and
 * `because` — the features that decided it.
 */
export interface SheetSurvey {
  sheet: string
  rows: number
  profile: string
  role: string
  header_row: number | null
  data_rows: number
  because: string[]
}

/** W38 (D178): a workbook's survey — the rules' id, the file's own published format if it is one, every sheet in order. */
export interface WorkbookSurvey {
  rule: Record<string, string | number>
  file: { profile: string; because: string[] } | null
  sheets: SheetSurvey[]
}

/** `POST /documents/{id}/survey`: `200` once it ended inside `wait`, else `202` with the job to follow. */
export interface SurveyAnswer {
  job_id: string
  status: string
  job: ParseJob
  survey: WorkbookSurvey | null
}

export interface SurveyInput {
  /** Omitted: the current version. */
  version_id?: string | null
  /** A protected workbook's, when its owner saved none. Sent once; never kept. */
  password?: string | null
  /** Seconds, 0 – 30 (the service's default 20). */
  wait?: number
}

/** FR-E7: whose books, the expected period and the expected identity — sealed on the job, never in an event. */
export interface ParseContext {
  gstins?: string[]
  pan?: string
  /** `2025-26`. */
  fy?: string
  /** `2026-27`. */
  ay?: string
  /** `YYYY-MM-DD`. */
  from?: string
  to?: string
}

/**
 * `POST /documents/{id}/parse` (FR-E2, FR-E3). A person's request is reviewed
 * in ProDoc (`review: auto`); `force` re-reads what was read, with a `reason`
 * (FR-E5); a `password` opens an encrypted PDF once and is kept nowhere (FR-E6).
 */
export interface ParseInput {
  /** A profile name (`invoice`, `bank_statement`, `form16`, …) or `auto`. */
  profile: string
  /** Omitted: the document's current version. */
  version_id?: string | null
  options?: Record<string, unknown>
  review?: ReviewMode
  context?: ParseContext | null
  password?: string | null
  force?: boolean
  reason?: string | null
  /** Seconds (≤ 30) the service may hold the answer for a job that ends quickly (FR-E9). */
  wait?: number
}

/**
 * A parse riding an upload (T15.15, FR-E2, FR-E40): the `parse` form field of
 * `POST /documents`, a JSON object, committed with each stored version or not
 * at all — so a refused reading stores nothing. Its version is the one being
 * stored, so it names none.
 */
export interface ParseOnUpload {
  /** A profile name — the document type's code — or `auto`. */
  profile: string
  /** `auto` (the default) is ProDoc's review; `app`, the host app's own (FR-E19). */
  review?: ReviewMode
  options?: Record<string, unknown>
  /** FR-E7: whose books — the party's GSTINs and PAN — and the expected period. */
  context?: ParseContext | null
}

/** The answer to a parse: the job, its extraction's record when it already ended, and whether it repeats an earlier request (FR-E5). */
export interface ParseAnswer {
  job: ParseJob
  extraction: ExtractionRecord | null
  repeat: boolean
}

/* ----------------------------------------------------------- profiles */

/** One profile as `GET /profiles` answers it (C-E3) — the org's view, switched-off ones `enabled: false`. */
export interface ParseProfile {
  /** The document-type code it reads (`invoice`, `form16`, …) — the same codes as the document types (FR-5, FR-45). */
  name: string
  schema_version: number | null
  /** `chain` or `form`. */
  mode: string | null
  media_types: string[]
  options: Record<string, unknown>
  enabled: boolean
  /** C-E1's flag: the plumbing's own profile (`_smoke`), never offered to a person. */
  internal: boolean
  /**
   * W32 (engine FR-E47): how each layout is read — `document`, `structured` or `mapped`;
   * a layout not named is `document`. Optional so a `ParseProfile` a host built before
   * W32 still is one; `profileListOf` always sets it (`{}` when the service said none).
   */
  readers?: Record<string, LayoutReader | string>
  /** W32 (FR-E47, FR-E48): the fields a column map may bind — `[]` unless the profile has a mapped layout. */
  map_fields?: MapField[]
  /** W32 (FR-E50, D123): the field paths that hold a list of rows, paged on read (`entries`). */
  list_paths?: string[]
}

/* ---------------------------------------------------------- column maps */

/** W32 (FR-E47): how a layout is read. */
export type LayoutReader = 'document' | 'structured' | 'mapped'

/**
 * What a mapped field holds (FR-E47): money is integer paise, a date `YYYY-MM-DD`; W38 (C-E23) a
 * `decimal` — a rate or a quantity, an exact decimal string (`"18"`, `"2.5"`).
 */
export type MapFieldKind = 'text' | 'date' | 'money' | 'integer' | 'code' | 'decimal'

/** One field a column map may bind (`map_fields`). */
export interface MapField {
  name: string
  kind: MapFieldKind | string
  required: boolean
}

/** The date formats a map may name (contract W32); `null` reads day-first as printed. */
export const MAP_DATE_FORMATS = ['DD/MM/YYYY', 'DD-MM-YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD', 'DD-MMM-YYYY', 'MM/DD/YYYY'] as const
export type MapDateFormat = (typeof MAP_DATE_FORMATS)[number]

/** `signed`: one `amount` column, positive a debit, negative a credit; `debit_credit`: a column each. */
export type MoneyMode = 'debit_credit' | 'signed'

/** A row to skip: blank, a repeated header, or a row whose cells hold this text (a total line). */
export type MapSkip = 'blank' | 'repeat_header' | `text:${string}`

export interface MapRules {
  date_format: MapDateFormat | null
  money: { mode: MoneyMode }
  skip: MapSkip[]
  /** A row whose cells hold this text ends the table. */
  stop_at: string | null
}

/**
 * What one field of a map is bound to (engine FR-E48, D119; W38 D176, C-E23):
 *
 * - **a column** — its letter (`C`, `AA`) or its header's text as printed (`header:Debit`);
 * - **one column per rate** — `rate:<head>` (`rate:taxable`): a register pivoted by rate
 *   (`5% Taxable`, `18% Taxable`, `9% CGST`) reads one line per rate, the rate taken from
 *   each column's heading and never derived (the kernel's rule, half rates included);
 * - **several columns, added** — a list of columns (`["F", "header:Software Exp 18%"]`): a
 *   taxable value spread over one column per expense ledger;
 * - **the ledger a line is posted to** (W38 follow-up, ProGST's D-48) — `{ledger_of: [columns]}`:
 *   a text field (a sales register's item) reads the heading of the one column among them
 *   holding a figure, nothing where none or two do.
 */
export type MapBinding = string | string[] | { ledger_of: string[] }

/**
 * A column map's spec (engine FR-E48, D119) — the shape the kernel validates, unknown
 * keys refused. **Data, never code.** A column is its letter (`C`, `AA`) or its
 * header's text as printed (`header:Debit`); from W38 a field may also read per rate or
 * add several columns (`MapBinding`).
 */
export interface MapSpec {
  /** A sheet's name; `null` = the first with content; a CSV's is `""`. */
  sheet: string | null
  /** 1-based. */
  header_row: number
  columns: Record<string, MapBinding>
  rules: MapRules
}

export interface ColumnMapVersion {
  number: number
  spec: MapSpec
  /** The 64-hex header fingerprint the map was made against — `null` for a map chosen by fit alone. */
  fingerprint: string | null
  created_at: string | null
  created_by: string | null
}

/** A saved column map (FR-E48): the org's, for one profile, under a scope, with its newest version. */
export interface ColumnMap {
  id: string
  profile: string
  /** `org` or `client:<id>`. */
  scope: string
  name: string
  owner_app: string
  import_key: string | null
  archived_at: string | null
  created_at: string | null
  updated_at: string | null
  /** The newest version; `null` only when the service sent none. */
  version: ColumnMapVersion | null
  /** On a read of one map only, newest first. */
  versions?: { number: number; fingerprint: string | null; created_at: string | null; created_by: string | null }[]
  /** An `import_key` already used by this owner answered the existing map (`meta.repeat`). Set only then. */
  repeat?: boolean
}

export interface ColumnMapInput {
  profile: string
  /** `org` or `client:<id>` (≤ 120 characters). */
  scope: string
  /** ≤ 120 characters. */
  name: string
  spec: MapSpec
  /** The preview's (or the header candidate's) fingerprint of the file the map was made against. */
  fingerprint?: string | null
  import_key?: string | null
}

export interface ColumnMapVersionInput {
  spec: MapSpec
  fingerprint?: string | null
}

export interface ColumnMapQuery {
  profile?: string
  scope?: string
  /** Default `false`: only live maps. */
  archived?: boolean
}

/** The ceilings the service holds a map's scope and name to. */
export const MAX_MAP_NAME = 120
export const MAX_MAP_SCOPE = 120

/** One sheet of the refused file (D121). */
export interface MappingSheet {
  name: string
  rows: number
}

/** A row that may be the header (D121): its cells as printed (≤ 60) and its fingerprint. */
export interface HeaderCandidate {
  sheet: string
  /** 1-based. */
  row: number
  cells: string[]
  fingerprint: string | null
}

/** `job.mapping` — what a person needs to map a file the service would not guess (FR-E49). */
export interface MappingNeeded {
  sheets: MappingSheet[]
  candidates: HeaderCandidate[]
  /** A map proposed from the profile's own headings vocabulary, or `null`. */
  suggestion: MapSpec | null
  /** W33 (FR-E56): the suggestion binds every required field. Set only where the service sent it. */
  complete?: boolean
  /** W33: the fields the vocabulary could not decide between. Set only where the service sent it. */
  ambiguous?: string[]
  /** W38 (D177): a register's proof, the reasons it was not read with no person — codes. */
  unproven?: string[]
  /** W38 follow-up: each `unproven` code's facts (the sheets, the count of rows, the cell), keyed by the code. */
  unproven_details?: Record<string, Record<string, unknown>>
  /** W38 follow-up: why each map offered did not fit — `{map: {id, version}, problem, …facts}`. */
  misfits?: MapMisfit[]
}

/**
 * W38 follow-up: why a saved map did not fit the file (`core.maps.Misfit`) — `problem` a code
 * (`no_sheet`, `not_on_sheet`, `no_header_row`, `column_missing`, `column_twice`, `no_numbers`,
 * `no_rate_columns`, `orphan_rate_column`, `rate_head_missing`, `fingerprint`, `map_invalid`;
 * a register's `names_more`, `says_more`, `several_places`, `ledgers_foot`) and its facts as sent.
 */
export interface MapMisfit {
  map: { id: string; version: number } | null
  problem: string
  facts: Record<string, unknown>
}

/** FR-E51: a row-shaped extraction's lists counted and its totals in paise. */
export interface RowSummary {
  lists: Record<string, number>
  totals: Record<string, number>
}

/** The preview (D122): the first ≤ 50 entries as read under an unsaved map, its summary and checks. */
export interface MapPreview {
  /** Money integer paise, dates `YYYY-MM-DD`, `null` where empty. */
  rows: Record<string, unknown>[]
  summary: RowSummary | null
  checks: Check[]
  reconciled: boolean
  /** The file's header row under this spec — sent with the save. */
  fingerprint: string | null
  headers: string[]
  /** More rows than 50 past the header. */
  truncated: boolean
}

export interface MapPreviewInput {
  document_id: string
  version_id?: string | null
  profile: string
  spec: MapSpec
  /** Seconds, 0 – 30 (the service's default 20). */
  wait?: number
}

/** `POST /column-maps/preview`: `200` once it ended inside `wait`, else `202` with the job to follow. */
export interface MapPreviewAnswer {
  job_id: string
  status: string
  job: ParseJob
  preview: MapPreview | null
}

/** A map an extraction was read under (FR-E48): its id and version, and on the full read its snapshot. */
export interface ColumnMapRef {
  id: string
  version: number | null
  spec?: MapSpec | null
}

/** `GET …/extractions/{eid}/rows` (FR-E50, D123): one page of a list. */
export interface ExtractionRows {
  path: string
  offset: number
  limit: number
  total: number
  items: Record<string, unknown>[]
  evidence: (Evidence | null)[]
}

export interface ExtractionRowsQuery {
  /** A list path the profile names (`list_paths`), e.g. `entries`. */
  path: string
  offset?: number
  /** ≤ 1,000. */
  limit?: number
}

/** The paged read's ceiling. */
export const MAX_ROWS_PAGE = 1000

/**
 * **Which profile reads a document of a type** (engine plan D54, W17) — a table, not
 * equality. A profile is named by the type it reads (FR-5, FR-45), so a type is read by
 * its own code — except a **credit note** and a **debit note**, which are read as
 * `invoice`, their kind in the reading's `sub_type`. *Read it as…* offers through it, and
 * an uploader's `parse` naming a type's code is sent as the profile that reads it. W37's
 * types — `gstr2a`, `gstr1`, `gst_portal_ledger`, `platform_settlement`, beside `gstr2b` — are
 * each read by the profile of their own name, so the table needs no row for them — nor do W38's
 * `sales_register`, `purchase_register` and `trial_balance`, each a type of its own name.
 */
export const PROFILE_FOR_TYPE: ReadonlyMap<string, string> = new Map([
  ['credit_note', 'invoice'],
  ['debit_note', 'invoice'],
])

/** The profile that reads a document typed `code`: the table's, else the code itself (`auto` stays `auto`). */
export const profileForType = (code: string): string => PROFILE_FOR_TYPE.get(code) ?? code

export interface ProfileList {
  items: ParseProfile[]
  /** `meta.ai_available`: whether a model may be asked here at all; `null` when unsaid. */
  ai_available: boolean | null
  /** `meta.offered`: the organisation's *parsing offered* (FR-E38); `null` when unsaid. */
  offered: boolean | null
  /** `meta.ai_processing`: `external` or `india_only` (FR-E31); `null` when unsaid. */
  ai_processing: string | null
}

/* --------------------------------------------------------- the record */

export type CheckResult = 'verified' | 'broken' | 'not_applicable'

/** One arithmetic invariant (C-E2). Money — `expected`, `found`, `delta` — in integer paise; a record's summary carries no figures. */
export interface Check {
  name: string
  result: CheckResult | string
  /** A gating check decides `reconciled`; an advisory one only informs. */
  gating: boolean
  expected: number | null
  found: number | null
  delta: number | null
  detail: string | null
  /**
   * W24 (engine FR-E46, contract C-E16, kernel 0.20.0): the field paths the check
   * read, in the evidence keys' syntax — `totals.taxable_value`, `items[2].amount` —
   * with `[*]` for a list family (`transactions[*].balance`); a broken check that
   * knows which rows broke names them concretely as well (`transactions[12].balance`).
   * `null` for a check read before W24, which said none. Optional so a `Check` a
   * host built before W24 still is one; `checkOf` always sets it.
   */
  fields?: string[] | null
}

/**
 * A check's field path (C-E16): names and indices, an index `[*]` for every row
 * of a list. Anything else the service might send is not a path, and is dropped.
 */
export const CHECK_FIELD_PATH = /^[A-Za-z_][A-Za-z0-9_]*(?:\[(?:[0-9]+|\*)\]|\.[A-Za-z_][A-Za-z0-9_]*)*$/

/**
 * Where the kernel read a field from (FR-E15). `json` and `text_export` (engine W18,
 * C-E13) are a file read **as structure** — an AIS's JSON download, a Form 26AS's
 * TRACES text export — with no page: the evidence is a JSON pointer or a line.
 */
export type EvidenceSource = 'text_layer' | 'table' | 'spreadsheet' | 'json' | 'text_export' | 'ocr' | 'ai_text' | 'ai_vision' | 'derived'

/** A box on a page: `[x0, top, x1, bottom]`, integers in **hundredths of a PDF point**, from the page's top-left (C-E6). */
export type Box = readonly [number, number, number, number]

/** Where a field was read (FR-E15, C-E2, C-E13): a page and a box, a cell, a JSON pointer, a line, a span — or, for a field the document does not show, the reason. */
export interface Evidence {
  source: EvidenceSource | string | null
  /** 1-based. */
  page: number | null
  box: Box | null
  /** A spreadsheet cell, `B7` or `Sheet1!B7`. */
  cell: string | null
  /**
   * A JSON file's RFC 6901 pointer, `/AIS/partB/0/amount` (engine W18, C-E13) — like a cell, no page.
   * Optional so an `Evidence` a host built before W18 still is one; `evidenceOf` always sets it.
   */
  pointer?: string | null
  /** A text export's line, 1-based (engine W18, C-E13) — like a cell, no page. Optional as `pointer` is. */
  line?: number | null
  /** W32 (FR-E50): a grid row read under a map — its sheet's name (a CSV's is `""`). Set only when the service sent it. */
  sheet?: string | null
  /** W32: that row, 1-based. Set only when the service sent it. */
  row?: number | null
  /** W32: each field's cell in that row, `{debit: "D7"}`. Set only when the service sent it. */
  cells?: Record<string, string> | null
  /** The text the figure was read from. */
  span: string | null
  /** A derived field's inputs, by field path. */
  inputs: string[] | null
  reason: string | null
}

/**
 * A page's size as the profile read it (engine T15.17): integers in **hundredths
 * of a PDF point**, the units of a box (C-E6). A scan's page is the page **as
 * turned upright and straightened** (FR-E15 as amended), so a turned page's
 * width and height are swapped against the file's. None for a spreadsheet, a JSON
 * file or a text export, whose evidence is cells, pointers and lines.
 */
export interface ExtractionPage {
  /** 1-based. */
  number: number
  width: number
  height: number
}

/**
 * One page of a scan's reading — the static rung's `scan` report (engine W14,
 * T14.3), as the viewer needs it to turn the page (T15.18).
 */
export interface ScanPage {
  /** 1-based. */
  page: number
  /** `ocr`, or `text_layer` for a searchable scan read from its own layer. */
  reader: string | null
  /** The quality profile: `clean`, `blurred`, `dark`, `noisy`, `skewed`, `unusable`. */
  quality: string | null
  /** Degrees the scan was turned **counter-clockwise** to read it upright: 0, 90, 180 or 270. */
  turned: number
  /** The skew straightened before reading, in hundredths of a degree, counter-clockwise positive — the report's `deskew:<n>` step; 0 for none. */
  deskew: number
}

/** One rung of the recovery ladder (FR-E27): whether it ran, whether its answer was adopted, and why not. */
export interface Rung {
  rung: string
  ran: boolean
  adopted: boolean
  calls: number
  /** `adopted`, `not_better`, `no_answer`, `apply_refused`, `withheld`, `unavailable`. */
  outcome: string | null
  profile: string | null
  /** The static rung of a scan: how each page was read (W14) — absent for a digital file. */
  scan?: ScanPage[]
}

/** FR-E19's states: an `auto` extraction is `accepted` or `needs_review`; an `app` one `app_review`; a decision makes it `accepted` or `rejected`. */
export type ExtractionReviewState = 'pending' | 'app_review' | 'needs_review' | 'accepted' | 'rejected'

export const EXTRACTION_REVIEW_STATES: ExtractionReviewState[] = ['pending', 'app_review', 'needs_review', 'accepted', 'rejected']

export interface ExtractionReviewInfo {
  mode: ReviewMode | string
  state: ExtractionReviewState | string
  /** FR-E19's reasons, in the service's order: `checks_failed`, `identity_weak`, … */
  reasons: string[]
  /** How many revisions of corrections it has had — 0 for none. */
  revision: number
  decided_by_user: string | null
  decided_by_app: string | null
  decided_party: string | null
  decided_at: string | null
  comment: string | null
  /** The revision whose corrected view the kernel last re-checked (D11). */
  rechecked_revision: number | null
  /** That recheck's names and results, without figures. */
  rechecks: Check[] | null
}

/** `GET /documents/{id}/extractions` row (FR-E13, FR-E18) — the record, no fields. */
export interface ExtractionRecord {
  id: string
  document_id: string
  version_id: string
  job_id: string | null
  owner_app: string
  profile: string
  /** For an `auto` request, the profile the engine found (FR-E25). */
  profile_found: string | null
  schema_version: number | null
  layout: string | null
  engine_version: string | null
  kernel_version: string | null
  /** `digital` or `scanned` (FR-E26). */
  channel: string | null
  mode: string | null
  /** Every gating check verified **and** something extracted (FR-E14). */
  reconciled: boolean
  rows_extracted: number | null
  checks: Check[]
  flags: string[]
  confidence: { money: number | null; identity: { score: number | null; band: string | null } }
  rungs: Rung[]
  cost: Record<string, unknown>
  timing: Record<string, unknown>
  payload_sha256: string | null
  /** A newer version of the document exists than the one it read (FR-E18). */
  stale: boolean
  current_version_id: string | null
  review: ExtractionReviewInfo | null
  created_at: string | null
  /** W32 (FR-E47): how its layout was read — `document`, `structured` or `mapped`; `null` before W32. Optional so a host's record from before W32 still is one. */
  reader?: LayoutReader | string | null
  /** W32 (FR-E48): the column map it was read under, or `null`. */
  column_map?: ColumnMapRef | null
}

/** One correction (FR-E21): a row of its own, never an edit of the extraction. */
export interface Correction {
  id: string
  revision: number
  field_path: string
  from: unknown
  to: unknown
  by_user: string | null
  by_app: string | null
  by_party: string | null
  reason: string | null
  created_at: string | null
}

/** `GET /documents/{id}/extractions/{eid}` once the review allows (FR-E19): the record and its content. */
export interface Extraction extends ExtractionRecord {
  fields: Record<string, unknown>
  /** By field path — `total`, `items[2].amount` — the same path a correction names. */
  evidence: Record<string, Evidence | null>
  /** The size of each page the boxes are on (T15.17) — empty for a spreadsheet, a JSON file or a text export, and for an extraction read before kernel 0.15. */
  pages: ExtractionPage[]
  identity_reasons: string[]
  corrections: Correction[]
  /** The accepted view — the fields with every correction applied — or `null` before any. */
  view: Record<string, unknown> | null
  /** The kernel's checks of that view (D11), with their figures; `null` until rechecked. */
  view_checks: Check[] | null
  /**
   * W23 (FR-51, C-W23): the field paths whose values the service **masked** for
   * this person — an employee's PAN, a statement's account number — with their
   * evidence's `span`. A sibling app's own `/internal` read is unmasked and has
   * none. Optional so an `Extraction` a host built before W23 still is one;
   * `extractionOf` always sets it.
   */
  masked_paths?: string[]
  /** W32 (FR-E51): a row-shaped profile's lists counted and its totals in paise, or `null`. */
  summary?: RowSummary | null
}

/** A correction's value: what a field holds — text, integer paise, yes / no, or nothing. Never a float (FR-E16). */
export type FieldValue = string | number | boolean | null

export interface CorrectionsInput {
  corrections: { field_path: string; value: FieldValue }[]
  /** Needed for an accepted extraction (FR-E23): a later correction is a new revision with a reason. */
  reason?: string | null
}

export interface CorrectionAnswer {
  extraction_id: string
  revision: number
  review: ExtractionReviewInfo | null
  /** The kernel's recheck of the corrected view (D11, interactive, unmetered). */
  recheck_job: ParseJob | null
}

export interface ExtractionDecisionInput {
  /** Up to 1,000 characters, recorded with the decision. */
  comment?: string | null
}

/** The comment's ceiling (`MAX_COMMENT`). */
export const MAX_EXTRACTION_COMMENT = 1000
/** A correction's reason's ceiling (`MAX_REASON`). */
export const MAX_CORRECTION_REASON = 500

/**
 * A row of the extraction review queue, `GET /extractions?state=needs_review`
 * (T15.5). **Assumed until the route exists**: the extraction's record and the
 * document it read — `{id, title, document_type, media_type, workspace_id,
 * owner_app}` under `document` — so the queue names what it lists.
 */
export interface ExtractionQueueItem extends ExtractionRecord {
  document: { id: string; title: string; document_type: string | null; media_type: string | null; workspace_id: string | null; owner_app: string | null } | null
}

export interface ExtractionQuery {
  state?: ExtractionReviewState
  profile?: string
  limit?: number
  offset?: number
}

/* ------------------------------------------------- a document's reading */

/**
 * W22 (FR-55, FR-57, FR-58; plan D52): where the reading of a document's
 * **current version** stands, computed by the service from its latest parse
 * job and that job's extraction — never stored, never guessed here:
 *
 * - `none` — nothing was asked (*Stored*);
 * - `reading` — queued or running;
 * - `needs_review` — read, and waiting for a person or for its app;
 * - `accepted` — read and accepted, by a reviewer or automatically;
 * - `failed` — refused, failed, or rejected by a reviewer — `reason` says which.
 */
export type ReadingState = 'none' | 'reading' | 'needs_review' | 'accepted' | 'failed'

/** The five, in the order a reading moves through them — the library's filter and the home screen's tiles. */
export const READING_STATES: readonly ReadingState[] = ['none', 'reading', 'needs_review', 'accepted', 'failed']

/** `document_out.reading` (C-W22). */
export interface DocumentReading {
  state: ReadingState
  /**
   * The job's reason (`password_required`, `password_incorrect`,
   * `profile_mismatch`, `timeout`, …), `app_review` for one its app reviews,
   * or `rejected` for a reading a reviewer turned down. `null` otherwise.
   */
  reason: string | null
  job_id: string | null
  extraction_id: string | null
}

/**
 * `document_out.password` (FR-58, D52), of the **current version**:
 * `not_required` (not encrypted), `locked` (encrypted, no completed reading),
 * `wrong` (the last unlock ended `password_incorrect`), `unlocked` (a reading
 * completed). W30 (FR-67, ADR-0021): `saved` — the version's password is
 * kept, sealed, and used for the reader (the package also reads it from
 * `password_saved`, `passwordSavedOf`). The stored file is never decrypted,
 * whichever it is.
 */
export type PasswordState = 'not_required' | 'locked' | 'wrong' | 'unlocked' | 'saved'

export const PASSWORD_STATES: readonly PasswordState[] = ['not_required', 'locked', 'wrong', 'unlocked', 'saved']

/* ---------------------------------------------------------- normalisers */

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
const str = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)
const int = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) ? value : null)
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [])

/**
 * `reading` as the service sent it — `null` when it sent none (a service older
 * than W22) or a state the package does not know, so a screen shows nothing
 * rather than a guess.
 */
export function readingOf(raw: unknown): DocumentReading | null {
  const r = asRecord(raw)
  const state = str(r?.state)
  if (!r || !state || !(READING_STATES as readonly string[]).includes(state)) return null
  return { state: state as ReadingState, reason: str(r.reason), job_id: str(r.job_id), extraction_id: str(r.extraction_id) }
}

/** `password` as the service sent it — `null` when absent or unknown. */
export function passwordStateOf(raw: unknown): PasswordState | null {
  return typeof raw === 'string' && (PASSWORD_STATES as readonly string[]).includes(raw) ? (raw as PasswordState) : null
}

export function jobOf(raw: unknown): ParseJob {
  const r = asRecord(raw) ?? {}
  const job: ParseJob = {
    id: str(r.id) ?? '',
    document_id: str(r.document_id) ?? '',
    version_id: str(r.version_id) ?? '',
    owner_app: str(r.owner_app) ?? '',
    kind: str(r.kind) ?? 'parse',
    profile: str(r.profile),
    options: asRecord(r.options) ?? {},
    review_mode: str(r.review_mode) ?? 'auto',
    lane: str(r.lane),
    status: str(r.status) ?? 'queued',
    reason: str(r.reason),
    suggested_profile: str(r.suggested_profile),
    attempts: int(r.attempts) ?? 0,
    residency: str(r.residency),
    extraction_id: str(r.extraction_id),
    forced: r.forced === true,
    created_at: str(r.created_at),
    started_at: str(r.started_at),
    finished_at: str(r.finished_at),
  }
  // W32 (D121, D122): only on the job that carries them — a refusal for want of a map, a preview.
  if (r.mapping !== undefined) job.mapping = mappingOf(r.mapping)
  if (r.preview !== undefined) job.preview = mapPreviewOf(r.preview)
  // W37 (D170): only on a completed split job.
  if (r.split !== undefined) job.split = splitAnswerOf(r.split)
  // W38 (D178): only on a completed survey job.
  if (r.survey !== undefined) job.survey = workbookSurveyOf(r.survey)
  // W19 (C-E26): only from a service that sends them — an older one reads as before.
  if (r.ai_route !== undefined) job.ai_route = str(r.ai_route)
  if (r.held_since !== undefined) job.held_since = str(r.held_since)
  if (r.waiting !== undefined) job.waiting = str(r.waiting)
  return job
}

/** W38 (D178): a survey as the service sent it — sheets with a name kept, counts whole, `null` when it is none. */
export function workbookSurveyOf(raw: unknown): WorkbookSurvey | null {
  const r = asRecord(raw)
  if (!r) return null
  const sheets: SheetSurvey[] = []
  for (const entry of Array.isArray(r.sheets) ? r.sheets : []) {
    const s = asRecord(entry)
    if (!s || typeof s.sheet !== 'string') continue
    const header = int(s.header_row)
    sheets.push({
      sheet: s.sheet,
      rows: nonNegative(s.rows) ?? 0,
      profile: str(s.profile) ?? 'other',
      role: str(s.role) ?? 'unrecognised',
      header_row: header !== null && header >= 1 ? header : null,
      data_rows: nonNegative(s.data_rows) ?? 0,
      because: strings(s.because),
    })
  }
  const file = asRecord(r.file)
  const rule = Object.fromEntries(Object.entries(asRecord(r.rule) ?? {}).filter((e): e is [string, string | number] => typeof e[1] === 'string' || typeof e[1] === 'number'))
  return { rule, file: file && str(file.profile) ? { profile: str(file.profile)!, because: strings(file.because) } : null, sheets }
}

/** The survey's answer: the job (to follow on a `202`) and, once it ended, its sheets. */
export function surveyAnswerOf(raw: unknown): SurveyAnswer {
  const r = asRecord(raw) ?? {}
  const job = jobOf(r.job ?? { id: r.job_id, status: r.status, kind: 'survey' })
  const survey = r.survey !== undefined && r.survey !== null ? workbookSurveyOf(r.survey) : (job.survey ?? null)
  return { job_id: str(r.job_id) ?? job.id, status: str(r.status) ?? job.status, job, survey }
}

export function checkOf(raw: unknown): Check | null {
  const r = asRecord(raw)
  if (!r || typeof r.name !== 'string' || !r.name) return null
  return {
    name: r.name,
    result: str(r.result) ?? 'not_applicable',
    gating: r.gating === true,
    expected: int(r.expected),
    found: int(r.found),
    delta: int(r.delta),
    detail: str(r.detail),
    // C-E16: what it read — `null` when the service said nothing (an extraction from before W24), never a guess.
    fields: Array.isArray(r.fields) ? strings(r.fields).filter((p) => CHECK_FIELD_PATH.test(p)) : null,
  }
}

const checksOf = (raw: unknown): Check[] => (Array.isArray(raw) ? raw.map(checkOf).filter((c): c is Check => c !== null) : [])

export function evidenceOf(raw: unknown): Evidence | null {
  const r = asRecord(raw)
  if (!r) return null
  const box = Array.isArray(r.box) && r.box.length === 4 && r.box.every((n) => typeof n === 'number' && Number.isFinite(n)) ? (r.box as unknown as Box) : null
  const line = int(r.line)
  const evidence: Evidence = {
    source: str(r.source),
    page: int(r.page),
    box,
    cell: str(r.cell),
    // RFC 6901: a pointer is empty (the whole file) or starts with `/` — anything else is not one.
    pointer: typeof r.pointer === 'string' && (r.pointer === '' || r.pointer.startsWith('/')) ? r.pointer : null,
    line: line !== null && line >= 1 ? line : null,
    span: str(r.span),
    inputs: Array.isArray(r.inputs) ? strings(r.inputs) : null,
    reason: str(r.reason),
  }
  // W32 (FR-E50): a grid row read under a map — its sheet, its row and each field's cell; absent elsewhere.
  if ('sheet' in r) evidence.sheet = typeof r.sheet === 'string' ? r.sheet : null
  if ('row' in r) {
    const row = int(r.row)
    evidence.row = row !== null && row >= 1 ? row : null
  }
  if ('cells' in r) {
    const cells = asRecord(r.cells)
    evidence.cells = cells ? Object.fromEntries(Object.entries(cells).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1].length > 0)) : null
  }
  return evidence
}

/** The quarter turns the scanned channel reads a page by (`scanned/read.py`, T14.15). */
const TURNS = [0, 90, 180, 270]
/** An applied straightening in the preprocessing log; `deskew_refused:<n>` was tried and not applied. */
const DESKEW_STEP = /^deskew:(-?[0-9]{1,5})$/

/** A page of the static rung's `scan` report: its turn as the key `turned`, its straightening from the log's `deskew:<n>`. */
export function scanPageOf(raw: unknown): ScanPage | null {
  const r = asRecord(raw)
  const page = r ? int(r.page) : null
  if (!r || page === null || page < 1) return null
  const turned = int(r.turned)
  const step = strings(r.steps)
    .map((s) => DESKEW_STEP.exec(s))
    .find((m) => m !== null)
  return {
    page,
    reader: str(r.reader),
    quality: str(r.quality),
    turned: turned !== null && TURNS.includes(turned) ? turned : 0,
    deskew: step ? Number(step[1]) : 0,
  }
}

/** A page's size (T15.17): positive integers in hundredths of a point, or nothing. */
export function extractionPageOf(raw: unknown): ExtractionPage | null {
  const r = asRecord(raw)
  if (!r) return null
  const number = int(r.number)
  const width = int(r.width)
  const height = int(r.height)
  if (number === null || width === null || height === null || number < 1 || width <= 0 || height <= 0) return null
  return { number, width, height }
}

function rungOf(raw: unknown): Rung | null {
  const r = asRecord(raw)
  if (!r || typeof r.rung !== 'string') return null
  const rung: Rung = { rung: r.rung, ran: r.ran === true, adopted: r.adopted === true, calls: int(r.calls) ?? 0, outcome: str(r.outcome), profile: str(r.profile) }
  if (Array.isArray(r.scan)) rung.scan = r.scan.map(scanPageOf).filter((p): p is ScanPage => p !== null)
  return rung
}

export function reviewInfoOf(raw: unknown): ExtractionReviewInfo | null {
  const r = asRecord(raw)
  if (!r) return null
  return {
    mode: str(r.mode) ?? 'auto',
    state: str(r.state) ?? 'pending',
    reasons: strings(r.reasons),
    revision: int(r.revision) ?? 0,
    decided_by_user: str(r.decided_by_user),
    decided_by_app: str(r.decided_by_app),
    decided_party: str(r.decided_party),
    decided_at: str(r.decided_at),
    comment: str(r.comment),
    rechecked_revision: int(r.rechecked_revision),
    rechecks: Array.isArray(r.rechecks) ? checksOf(r.rechecks) : null,
  }
}

export function extractionRecordOf(raw: unknown): ExtractionRecord {
  const r = asRecord(raw) ?? {}
  const confidence = asRecord(r.confidence) ?? {}
  const identity = asRecord(confidence.identity) ?? {}
  return {
    id: str(r.id) ?? '',
    document_id: str(r.document_id) ?? '',
    version_id: str(r.version_id) ?? '',
    job_id: str(r.job_id),
    owner_app: str(r.owner_app) ?? '',
    profile: str(r.profile) ?? '',
    profile_found: str(r.profile_found),
    schema_version: int(r.schema_version),
    layout: str(r.layout),
    engine_version: str(r.engine_version),
    kernel_version: str(r.kernel_version),
    channel: str(r.channel),
    mode: str(r.mode),
    reconciled: r.reconciled === true,
    rows_extracted: int(r.rows_extracted),
    checks: checksOf(r.checks),
    flags: strings(r.flags),
    confidence: { money: int(confidence.money), identity: { score: int(identity.score), band: str(identity.band) } },
    rungs: Array.isArray(r.rungs) ? r.rungs.map(rungOf).filter((x): x is Rung => x !== null) : [],
    cost: asRecord(r.cost) ?? {},
    timing: asRecord(r.timing) ?? {},
    payload_sha256: str(r.payload_sha256),
    stale: r.stale === true,
    current_version_id: str(r.current_version_id),
    review: reviewInfoOf(r.review),
    created_at: str(r.created_at),
    // W32 (FR-E47, FR-E48): `null` from a service before W32, which said neither.
    reader: str(r.reader),
    column_map: columnMapRefOf(r.column_map),
  }
}

function correctionOf(raw: unknown): Correction | null {
  const r = asRecord(raw)
  if (!r || typeof r.field_path !== 'string') return null
  return {
    id: str(r.id) ?? '',
    revision: int(r.revision) ?? 0,
    field_path: r.field_path,
    from: r.from ?? null,
    to: r.to ?? null,
    by_user: str(r.by_user),
    by_app: str(r.by_app),
    by_party: str(r.by_party),
    reason: str(r.reason),
    created_at: str(r.created_at),
  }
}

/** The full read, with W23's `meta.masked_paths` (or the same key on the body, from a host that folded `meta` in). */
export function extractionOf(raw: unknown, meta: Record<string, unknown> = {}): Extraction {
  const r = asRecord(raw) ?? {}
  const evidence: Record<string, Evidence | null> = {}
  for (const [path, value] of Object.entries(asRecord(r.evidence) ?? {})) evidence[path] = evidenceOf(value)
  return {
    ...extractionRecordOf(r),
    fields: asRecord(r.fields) ?? {},
    evidence,
    pages: Array.isArray(r.pages) ? r.pages.map(extractionPageOf).filter((p): p is ExtractionPage => p !== null) : [],
    identity_reasons: strings(r.identity_reasons),
    corrections: Array.isArray(r.corrections) ? r.corrections.map(correctionOf).filter((c): c is Correction => c !== null) : [],
    view: asRecord(r.view),
    view_checks: Array.isArray(r.view_checks) ? checksOf(r.view_checks) : null,
    masked_paths: maskedPathsOf(meta.masked_paths ?? r.masked_paths),
    summary: rowSummaryOf(r.summary),
  }
}

export function parseAnswerOf(body: unknown, meta: Record<string, unknown>): ParseAnswer {
  const r = asRecord(body) ?? {}
  const job = jobOf(r.job ?? { id: r.job_id, status: r.status })
  return { job, extraction: r.extraction ? extractionRecordOf(r.extraction) : null, repeat: meta.repeat === true }
}

export function correctionAnswerOf(body: unknown): CorrectionAnswer {
  const r = asRecord(body) ?? {}
  return {
    extraction_id: str(r.extraction_id) ?? '',
    revision: int(r.revision) ?? 0,
    review: reviewInfoOf(r.review),
    recheck_job: r.recheck_job ? jobOf(r.recheck_job) : null,
  }
}

export function queueItemOf(raw: unknown): ExtractionQueueItem {
  const r = asRecord(raw) ?? {}
  const d = asRecord(r.document)
  return {
    ...extractionRecordOf(r),
    document: d
      ? {
          id: str(d.id) ?? str(r.document_id) ?? '',
          title: str(d.title) ?? '',
          document_type: str(d.document_type),
          media_type: str(d.media_type),
          workspace_id: str(d.workspace_id),
          owner_app: str(d.owner_app),
        }
      : str(r.document_title)
        ? { id: str(r.document_id) ?? '', title: str(r.document_title)!, document_type: null, media_type: null, workspace_id: null, owner_app: null }
        : null,
  }
}

/** C-E3's list: `{data: [...]}` with `meta.ai_available`, `meta.offered` and `meta.ai_processing`. */
export function profileListOf(body: unknown, meta: Record<string, unknown>): ProfileList {
  const raw = Array.isArray(body) ? body : Array.isArray(asRecord(body)?.profiles) ? (asRecord(body)!.profiles as unknown[]) : []
  const items: ParseProfile[] = []
  for (const entry of raw) {
    const r = asRecord(entry)
    if (!r || typeof r.name !== 'string' || !r.name) continue
    items.push({
      name: r.name,
      schema_version: int(r.schema_version),
      mode: str(r.mode),
      media_types: strings(r.media_types),
      options: asRecord(r.options) ?? {},
      enabled: r.enabled !== false,
      // The plumbing's own profiles are named with a leading underscore (C-E1's `_smoke`) when the flag is absent.
      internal: r.internal === true || r.name.startsWith('_'),
      // W32 (FR-E47): an answer from before W32 has none of the three — every layout a document's, nothing to map.
      readers: Object.fromEntries(Object.entries(asRecord(r.readers) ?? {}).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1].length > 0)),
      map_fields: Array.isArray(r.map_fields) ? r.map_fields.map(mapFieldOf).filter((f): f is MapField => f !== null) : [],
      list_paths: strings(r.list_paths),
    })
  }
  return {
    items,
    ai_available: typeof meta.ai_available === 'boolean' ? meta.ai_available : null,
    offered: typeof meta.offered === 'boolean' ? meta.offered : null,
    ai_processing: str(meta.ai_processing),
  }
}

/** The body of a parse request: only what was given, the reason trimmed. The password travels in this one body and nowhere else. */
export function parseBody(input: ParseInput): Record<string, unknown> {
  const body: Record<string, unknown> = { profile: input.profile.trim() }
  if (input.version_id) body.version_id = input.version_id
  if (input.options && Object.keys(input.options).length > 0) body.options = input.options
  if (input.review) body.review = input.review
  if (input.context) {
    const context = Object.fromEntries(Object.entries(input.context).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)))
    if (Object.keys(context).length > 0) body.context = context
  }
  if (input.password) body.password = input.password
  if (input.force) {
    body.force = true
    body.reason = (input.reason ?? '').trim()
  }
  if (input.wait) body.wait = input.wait
  return body
}

/* ------------------------------------------------- column maps, read */

const nonNegative = (value: unknown): number | null => {
  const n = int(value)
  return n !== null && n >= 0 ? n : null
}

/** A column reference as the contract has it: a letter (`C`, `AA`) or `header:<text as printed>`. */
export const COLUMN_REF = /^(?:[A-Z]{1,3}|header:.+)$/s
/** W38 (D176): a field read per rate — `rate:<head>`, the head a word (`taxable`, `igst`); its vocabulary is the kernel's. */
export const RATE_REF = /^rate:.{1,120}$/s
/** W38 (D176): the prefix of a per-rate binding. */
export const RATE_PREFIX = 'rate:'
/** W38 (D176): a summed binding adds at most this many columns (the kernel's bound). */
export const MAX_SUMMED_COLUMNS = 60

/** A binding as the service sent it, or `null` when it is none of the contract's four. */
export function mapBindingOf(raw: unknown): MapBinding | null {
  if (typeof raw === 'string') return COLUMN_REF.test(raw) || RATE_REF.test(raw) ? raw : null
  const ledger = asRecord(raw)
  if (ledger && !Array.isArray(raw)) {
    const refs = Array.isArray(ledger.ledger_of) ? mapBindingOf(ledger.ledger_of) : null
    return Object.keys(ledger).length === 1 && Array.isArray(refs) ? { ledger_of: refs } : null
  }
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_SUMMED_COLUMNS) return null
  const refs = raw.filter((r): r is string => typeof r === 'string' && COLUMN_REF.test(r))
  return refs.length === raw.length ? refs : null
}

function mapFieldOf(raw: unknown): MapField | null {
  const r = asRecord(raw)
  if (!r || typeof r.name !== 'string' || !r.name) return null
  return { name: r.name, kind: str(r.kind) ?? 'text', required: r.required === true }
}

const SKIP_RULE = /^(?:blank|repeat_header|text:.+)$/s

/**
 * A map's spec as the service sent it (D119), or `null` when it is not one. What is
 * read is only what the contract names, so a spec read here can be sent back as it is.
 */
export function mapSpecOf(raw: unknown): MapSpec | null {
  const r = asRecord(raw)
  if (!r) return null
  const rules = asRecord(r.rules) ?? {}
  const money = asRecord(rules.money) ?? {}
  const header = int(r.header_row)
  const columns: Record<string, MapBinding> = {}
  for (const [field, ref] of Object.entries(asRecord(r.columns) ?? {})) {
    const binding = mapBindingOf(ref)
    if (binding !== null) columns[field] = binding
  }
  const format = typeof rules.date_format === 'string' && (MAP_DATE_FORMATS as readonly string[]).includes(rules.date_format) ? (rules.date_format as MapDateFormat) : null
  return {
    // A CSV's sheet is `""` — kept, not read as "none".
    sheet: typeof r.sheet === 'string' ? r.sheet : null,
    header_row: header !== null && header >= 1 ? header : 1,
    columns,
    rules: {
      date_format: format,
      money: { mode: money.mode === 'signed' ? 'signed' : 'debit_credit' },
      skip: strings(rules.skip).filter((s): s is MapSkip => SKIP_RULE.test(s)),
      stop_at: str(rules.stop_at),
    },
  }
}

/** The spec as a request carries it — exactly the contract's keys, so the service's `extra="forbid"` never refuses it for one of ours. */
export function mapSpecBody(spec: MapSpec): MapSpec {
  return {
    sheet: spec.sheet,
    header_row: spec.header_row,
    columns: Object.fromEntries(Object.entries(spec.columns).map(([f, b]) => [f, Array.isArray(b) ? [...b] : typeof b === 'string' ? b : { ledger_of: [...b.ledger_of] }])),
    rules: {
      date_format: spec.rules.date_format,
      money: { mode: spec.rules.money.mode },
      skip: [...spec.rules.skip],
      stop_at: spec.rules.stop_at,
    },
  }
}

/** `job.mapping` (D121): what the refusal carried — empty lists rather than a guess where it carried less. */
export function mappingOf(raw: unknown): MappingNeeded | null {
  const r = asRecord(raw)
  if (!r) return null
  const sheets: MappingSheet[] = []
  for (const entry of Array.isArray(r.sheets) ? r.sheets : []) {
    const s = asRecord(entry)
    if (s && typeof s.name === 'string') sheets.push({ name: s.name, rows: nonNegative(s.rows) ?? 0 })
  }
  const candidates: HeaderCandidate[] = []
  for (const entry of Array.isArray(r.candidates) ? r.candidates : []) {
    const c = asRecord(entry)
    const row = c ? int(c.row) : null
    if (!c || row === null || row < 1) continue
    candidates.push({
      sheet: typeof c.sheet === 'string' ? c.sheet : '',
      row,
      // A blank cell stays a cell: its place is its column's letter.
      cells: Array.isArray(c.cells) ? c.cells.map((v) => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v))) : [],
      fingerprint: str(c.fingerprint),
    })
  }
  const out: MappingNeeded = { sheets, candidates, suggestion: r.suggestion ? mapSpecOf(r.suggestion) : null }
  if (typeof r.complete === 'boolean') out.complete = r.complete
  if (Array.isArray(r.ambiguous)) out.ambiguous = strings(r.ambiguous)
  if (Array.isArray(r.unproven)) out.unproven = strings(r.unproven)
  const details = asRecord(r.unproven_details)
  if (details) out.unproven_details = Object.fromEntries(Object.entries(details).map(([code, facts]) => [code, asRecord(facts) ?? {}]))
  if (Array.isArray(r.misfits)) {
    out.misfits = r.misfits.flatMap((entry): MapMisfit[] => {
      const m = asRecord(entry)
      if (!m || typeof m.problem !== 'string') return []
      const map = asRecord(m.map)
      const version = map ? int(map.version) : null
      const { map: _map, problem: _problem, ...facts } = m
      return [{ map: map && typeof map.id === 'string' && version !== null ? { id: map.id, version } : null, problem: m.problem, facts }]
    })
  }
  return out
}

/** FR-E51's summary: counts and paise, whole numbers only — anything else is dropped, never rounded. */
export function rowSummaryOf(raw: unknown): RowSummary | null {
  const r = asRecord(raw)
  if (!r) return null
  const wholes = (value: unknown): Record<string, number> =>
    Object.fromEntries(Object.entries(asRecord(value) ?? {}).filter((e): e is [string, number] => typeof e[1] === 'number' && Number.isSafeInteger(e[1])))
  return { lists: wholes(r.lists), totals: wholes(r.totals) }
}

export function mapPreviewOf(raw: unknown): MapPreview | null {
  const r = asRecord(raw)
  if (!r) return null
  return {
    rows: Array.isArray(r.rows) ? r.rows.map(asRecord).filter((x): x is Record<string, unknown> => x !== null) : [],
    summary: rowSummaryOf(r.summary),
    checks: checksOf(r.checks),
    reconciled: r.reconciled === true,
    fingerprint: str(r.fingerprint),
    headers: Array.isArray(r.headers) ? r.headers.map((v) => (typeof v === 'string' ? v : '')) : [],
    truncated: r.truncated === true,
  }
}

/** The preview's answer: the job (to follow on a `202`) and, once it ended, its reading. */
export function mapPreviewAnswerOf(raw: unknown): MapPreviewAnswer {
  const r = asRecord(raw) ?? {}
  const job = jobOf(r.job ?? { id: r.job_id, status: r.status, kind: 'preview' })
  const preview = r.preview !== undefined && r.preview !== null ? mapPreviewOf(r.preview) : (job.preview ?? null)
  return { job_id: str(r.job_id) ?? job.id, status: str(r.status) ?? job.status, job, preview }
}

function columnMapRefOf(raw: unknown): ColumnMapRef | null {
  const r = asRecord(raw)
  if (!r || !str(r.id)) return null
  const ref: ColumnMapRef = { id: str(r.id)!, version: int(r.version) }
  if (r.spec !== undefined) ref.spec = mapSpecOf(r.spec)
  return ref
}

function mapVersionOf(raw: unknown): ColumnMapVersion | null {
  const r = asRecord(raw)
  const spec = r ? mapSpecOf(r.spec) : null
  if (!r || !spec) return null
  return { number: int(r.number) ?? 1, spec, fingerprint: str(r.fingerprint), created_at: str(r.created_at), created_by: str(r.created_by) }
}

/** A saved map (FR-E48), its newest version and — on a read of one — its versions. */
export function columnMapOf(raw: unknown, meta: Record<string, unknown> = {}): ColumnMap {
  const r = asRecord(raw) ?? {}
  const map: ColumnMap = {
    id: str(r.id) ?? '',
    profile: str(r.profile) ?? '',
    scope: str(r.scope) ?? 'org',
    name: str(r.name) ?? '',
    owner_app: str(r.owner_app) ?? '',
    import_key: str(r.import_key),
    archived_at: str(r.archived_at),
    created_at: str(r.created_at),
    updated_at: str(r.updated_at),
    version: mapVersionOf(r.version),
  }
  if (Array.isArray(r.versions)) {
    map.versions = r.versions
      .map(asRecord)
      .filter((v): v is Record<string, unknown> => v !== null && int(v.number) !== null)
      .map((v) => ({ number: int(v.number)!, fingerprint: str(v.fingerprint), created_at: str(v.created_at), created_by: str(v.created_by) }))
  }
  if (meta.repeat === true) map.repeat = true
  return map
}

/** One page of a list (D123). */
export function extractionRowsOf(raw: unknown, asked: ExtractionRowsQuery): ExtractionRows {
  const r = asRecord(raw) ?? {}
  const items = Array.isArray(r.items) ? r.items.map((x) => asRecord(x) ?? {}) : []
  return {
    path: str(r.path) ?? asked.path,
    offset: nonNegative(r.offset) ?? asked.offset ?? 0,
    limit: nonNegative(r.limit) ?? asked.limit ?? items.length,
    total: nonNegative(r.total) ?? items.length,
    items,
    evidence: Array.isArray(r.evidence) ? r.evidence.map(evidenceOf) : items.map(() => null),
  }
}

/* ------------------------------------------------------- whose books */

/**
 * The shapes the service's `ContextIn` accepts (FR-E7) — a GSTIN is two
 * digits then thirteen capitals or digits, a PAN five capitals, four digits
 * and a capital. **Shapes only**: whether a GSTIN's check digit holds is the
 * engine's identity check, never guessed here.
 */
export const GSTIN_SHAPE = /^[0-9]{2}[A-Z0-9]{13}$/
export const PAN_SHAPE = /^[A-Z]{5}[0-9]{4}[A-Z]$/

/**
 * T15.16: whose books a person names when asking for a reading — GSTINs
 * (one or more, separated by commas or spaces) and a PAN, upper-cased — as
 * the request's `context`, or what is wrong with them. Both empty is no
 * context at all: the engine then reads an invoice's side as `unknown`.
 */
export function partyContextOf(gstinText: string, panText: string): { context: ParseContext | null } | { errors: { gstin?: string; pan?: string } } {
  const gstins = [...new Set(gstinText.toUpperCase().split(/[\s,;]+/).filter(Boolean))]
  const pan = panText.trim().toUpperCase()
  const errors: { gstin?: string; pan?: string } = {}
  const badGstin = gstins.find((g) => !GSTIN_SHAPE.test(g))
  if (badGstin) errors.gstin = badGstin.length !== 15 ? `A GSTIN is 15 characters — “${badGstin}” has ${badGstin.length}.` : `“${badGstin}” is not shaped like a GSTIN: two digits, then thirteen capitals or digits.`
  if (gstins.length > 50) errors.gstin = 'Up to 50 GSTINs.'
  if (pan && !PAN_SHAPE.test(pan)) errors.pan = pan.length !== 10 ? `A PAN is 10 characters — this has ${pan.length}.` : 'Not shaped like a PAN: five capitals, four digits and a capital.'
  if (errors.gstin || errors.pan) return { errors }
  if (gstins.length === 0 && !pan) return { context: null }
  return { context: { ...(gstins.length ? { gstins } : {}), ...(pan ? { pan } : {}) } }
}
